import { Fragment, FunctionComponent, useEffect } from "react";
import { Hero } from "@App/Components/Hero";
import { BrandFilter } from "@App/Components/Filters";
import { ContactUs } from "@App/Components/ContactUs";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { useNetwork } from "@App/hooks/api/useNetwork";
import { useServices } from "@App/hooks/api/useServices";
import { useTranslation } from "react-i18next";
import { useHome } from "@App/hooks/useHome";
import { ServiceIsInspectionOrServiceIsAppointmentOrServiceIsEVehicleAppointment } from "@App/utils/conditionals/combinations";
import { ServiceIsGlass, ServiceIsWheelChange } from "@App/utils/conditionals/services";
import { Loader } from "@App/Components/Loader";
import { Brands } from "@App/types/enums";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import { SubServiceStep } from "@App/Pages/Partner/Partials/SubServiceStep";
import { VehicleTypeStep } from "@App/Pages/Partner/Partials/VehicleTypeStep";
import { EngineTypeStep } from "@App/Pages/Partner/Partials/EngineTypeStep";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { BrandIs } from "@App/utils/conditionals/brands";
import { ChooseDifferentCriteriaHintStep } from "@App/Pages/Partner/Partials/ChooseDifferentCriteriaHintStep";
import { Step } from "@App/Components/Stepper/Step";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const { t } = useTranslation();

  // step definitions
  const locationStepProps = useLocationStepDefinition(onSubmit);

  const { loading, error, services } = useServices();

  // fetches the network for the selected service and brand and stores it in the state
  const { loading: networkLoading } = useNetwork();

  const {
    currentStep,
    setCurrentStep,
    selectBrand,
    selectEngine,
    selectCommercialVehicle,
    togglePremiumService,
    togglePremiumServiceCarReplacementOrPickup,
    toggled,
    nextStep,
    selectService,
    organization,
    service,
    availableSubservices,
    availableSubservicesLoading,
    premiumServices,
    bevSupport,
    brand,
    commercialVehicleSupport,
  } = useHome();

  // treat the sub services as loading while the network for the selected brand is still being looked up,
  // so the premium services of the wrong network never show up
  const subServicesLoading = availableSubservicesLoading || networkLoading;

  useEffect(() => {
    if (currentStep === 0 && service) {
      selectService(service, true);
    }
  }, [service]);

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", { organization: organization.name })}
        backgroundImage={
          organization.settings.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service && service.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              loading={loading}
              services={services}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          {!ServiceIsGlass(service) && !subServicesLoading && (
            <Step
              title={t("Fahrzeugart")}
              subtitle={null}
              completedTitle={t("Fahrzeugart") + ": "}
              completedGoBackLinkTitle={commercialVehicleSupport ? t("Nutzfahrzeug") : t("PKW")}
            >
              <VehicleTypeStep selectCommercialVehicle={selectCommercialVehicle} />
            </Step>
          )}
          {!ServiceIsGlass(service) && !subServicesLoading && (
            <Step
              title={t("Antriebsart")}
              subtitle={null}
              completedTitle={t("Antriebsart") + ": "}
              completedGoBackLinkTitle={
                bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")
              }
            >
              <Fragment>
                <div className="overflow-hidden">
                  {ServiceIsInspectionOrServiceIsAppointmentOrServiceIsEVehicleAppointment(
                    service,
                  ) && <EngineTypeStep selectEngine={selectEngine} />}
                  {ServiceIsWheelChange(service) && <EngineTypeStep selectEngine={selectEngine} />}
                </div>
              </Fragment>
            </Step>
          )}
          {ServiceIsInspectionOrServiceIsAppointmentOrServiceIsEVehicleAppointment(service) &&
            !subServicesLoading && (
              <Step
                title={t("Waehlen Sie Ihre Marke.")}
                completedTitle={"Marke: "}
                completedGoBackLinkTitle={brand ? brand.name : ""}
              >
                <Fragment>
                  <BrandFilter onSubmit={selectBrand} />
                </Fragment>
              </Step>
            )}
          {ServiceIsInspectionOrServiceIsAppointmentOrServiceIsEVehicleAppointment(service) &&
            !subServicesLoading &&
            BrandIs(Brands.POLESTAR, brand) && (
              <Step title={t("")} completedTitle={""} completedGoBackLinkTitle={""}>
                <Fragment>
                  <ChooseDifferentCriteriaHintStep translationKey={"homeHints.polestar"} />
                </Fragment>
              </Step>
            )}
          {subServicesLoading && <Loader></Loader>}
          {!(
            ServiceIsInspectionOrServiceIsAppointmentOrServiceIsEVehicleAppointment(service) &&
            BrandIs(Brands.POLESTAR, brand)
          ) &&
            (availableSubservices?.length > 0 || service == null) &&
            !subServicesLoading && (
              <Step
                title={t("chooseSubServices")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices.length == 0 ? "Keine Auswahl" : ""} ${premiumServices.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableSubservices}
                  premiumServices={premiumServices}
                  toggled={toggled}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={togglePremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {!(
            ServiceIsInspectionOrServiceIsAppointmentOrServiceIsEVehicleAppointment(service) &&
            BrandIs(Brands.POLESTAR, brand)
          ) && <LocationStepDefinition {...locationStepProps} />}
        </Stepper>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              <Benefits organization={organization} />
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
                <div className={"mt-3"}>
                  Es ist etwas dazwischen gekommen?{" "}
                  <a
                    href={
                      "mailto:werkstattmeister@repareo.de?subject=Ayvens%20-%20Stornierung%20meines%20Werkstatttermins&body=Werkstattname%3A%20BITTE%20AUSF%C3%9CLLEN%0D%0ATag%20des%20Termins%3A%20BITTE%20AUSF%C3%9CLLEN%0D%0ATermin%20gebucht%20auf%20diesen%20Namen%3A%20BITTE%20AUSF%C3%9CLLEN"
                    }
                    title={"Termin absagen"}
                  >
                    Termin absagen
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
